import { motion } from "framer-motion";
import { toast, Toaster } from "sonner";
import { useLang, tx } from "@/context/LangContext";
import { EMAIL, SOCIALS, T } from "@/data/content";
import { scrollToId } from "@/lib/scroll";
import { FadeUp, MaskLine, SectionLabel } from "@/components/Reveal";
import ContactForm from "@/components/ContactForm";
import { ArrowUp, Copy } from "lucide-react";

export default function Contact() {
    const { lang } = useLang();

    const copyEmail = async () => {
        try {
            await navigator.clipboard.writeText(EMAIL);
            toast.success(tx(lang, T.contact.copied), {
                duration: 2600,
                style: {
                    background: "#131318",
                    color: "#FAF9F6",
                    border: "1px solid rgba(255,255,255,0.15)",
                    borderRadius: 0,
                    fontFamily: "'JetBrains Mono', monospace",
                    letterSpacing: "0.15em",
                    fontSize: "11px",
                },
            });
        } catch {
            toast(EMAIL);
        }
    };

    return (
        <section
            id="contact"
            data-testid="contact-section"
            className="relative overflow-hidden bg-ink pt-24 text-cream md:pt-36"
        >
            <div
                className="pointer-events-none absolute inset-0 opacity-40"
                style={{
                    background:
                        "radial-gradient(50% 45% at 85% 90%, rgba(138,43,226,0.18) 0%, transparent 70%)",
                }}
            />
            <div className="relative mx-auto max-w-[1600px] px-5 md:px-10">
                <SectionLabel en={tx(lang, T.contact.label)} fr={tx(lang, T.contact.label)} />

                <h2 className="mt-10 md:mt-14" data-testid="contact-title">
                    <MaskLine delay={0.05}>
                        <span className="block font-display text-[clamp(3rem,10.5vw,9.5rem)] font-extrabold uppercase leading-[0.9] tracking-[-0.02em]">
                            {tx(lang, T.contact.title1)}
                        </span>
                    </MaskLine>
                    <MaskLine delay={0.18}>
                        <span className="block pb-4 font-serifit text-[clamp(2.2rem,7.5vw,7rem)] italic leading-[1.1] text-violet">
                            {tx(lang, T.contact.title2)}
                        </span>
                    </MaskLine>
                </h2>

                <FadeUp delay={0.3} className="mt-10 md:mt-14">
                    <div className="grid gap-12 lg:grid-cols-2 lg:gap-16">
                        <div className="flex flex-col justify-between gap-10">
                            <div>
                                <p className="max-w-md text-sm font-light text-cream/60 md:text-base">
                                    {tx(lang, T.contact.sub)}
                                </p>
                                <a
                                    data-testid="contact-email-copy"
                                    data-cursor="MAIL"
                                    href={`mailto:${EMAIL}`}
                                    onClick={(e) => {
                                        // copie dans le presse-papier + ouvre le client mail
                                        copyEmail();
                                    }}
                                    className="group mt-8 flex items-center gap-4 border border-cream/25 px-5 py-4 font-mono text-[10px] tracking-[0.15em] transition-colors duration-300 hover:border-violet md:px-8 md:text-[11px] md:tracking-[0.25em]"
                                >
                                    {EMAIL}
                                    <Copy size={13} className="shrink-0 text-cream/50 transition-colors group-hover:text-violet" />
                                </a>
                                <FadeUp delay={0.1}>
                                    <p className="mt-10 font-mono text-[9px] tracking-[0.35em] text-cream/40 md:text-[10px]">
                                        {tx(lang, T.form.directTitle)}
                                    </p>
                                </FadeUp>
                            </div>
                            <FadeUp delay={0.4} className="flex flex-wrap gap-4 md:gap-6">
                                {SOCIALS.map((s) => (
                                    <a
                                        key={s.testid}
                                        data-testid={s.testid}
                                        href={s.href}
                                        target="_blank"
                                        rel="noopener noreferrer"
                                        className="link-line font-mono text-[10px] tracking-[0.25em] text-cream/70 transition-colors hover:text-cream md:text-[11px]"
                                    >
                                        {s.label} ↗
                                    </a>
                                ))}
                            </FadeUp>
                        </div>
                        <ContactForm />
                    </div>
                </FadeUp>

                <footer className="mt-20 flex flex-col items-start justify-between gap-6 border-t border-white/10 py-8 md:mt-28 md:flex-row md:items-center">
                    <p className="font-mono text-[9px] tracking-[0.3em] text-cream/40 md:text-[10px]">
                        © 2025 GLOOM ENT. — DIRECTED BY CASPER
                    </p>
                    <p className="font-mono text-[9px] tracking-[0.3em] text-cream/40 md:text-[10px]">
                        GENEVA, SWITZERLAND — WORLDWIDE
                    </p>
                    <button
                        data-testid="back-to-top-btn"
                        onClick={() => {
                            const lenis = window.__lenis || window.lenis;
                            if (lenis) lenis.scrollTo(0, { force: true });
                            else window.scrollTo({ top: 0, behavior: "smooth" });
                        }}
                        className="flex items-center gap-3 font-mono text-[9px] tracking-[0.3em] text-cream/60 transition-colors hover:text-cream md:text-[10px]"
                    >
                        {tx(lang, T.contact.top)}
                        <span className="flex h-8 w-8 items-center justify-center rounded-full border border-cream/25">
                            <ArrowUp size={12} />
                        </span>
                    </button>
                </footer>
            </div>
            <Toaster position="bottom-center" />
        </section>
    );
}
